import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import {
    STAxPos, STBarDir, STBarGrouping, STDLblPos, STOrientationDmlC,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { CHART_TYPE } from '@tencent/chart/lib/chart-ui/interface';
import {
    Axis,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/axis';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import {
    Coordinate,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/coordinate';
import flatten from 'lodash/flatten';
import { isEmptyValue } from '../utils';
import { filterOriginStyle, generateCustomStyles, NO_SUPPORT_FILL_SYMBOL } from '../utils/style';
import { ChartUILabelPosition, CustomStyles, ShapeOriginStyle } from '../interface/chart-ui.interface';
import { labelPositionTypeMap } from '../chart-config/map';
import type { AxisGridCfg, CoordinateOption } from '@tencent/chart/lib/antv/g2/interface';
import type { ChartUIDataItem, ShapeStyle } from '../interface/chart-ui.interface';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type { IChartScale, GeometryType, IChartRendererConfig, IChartAdjust } from '@tencent/chart/lib/chart-ui/interface';
import type {
    IBarChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/bar-chart.interface';
import type {
    IChartManualLayout,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import { LineJoin } from '@tencent/alloy-renderer';

export const BAR_CHART_TYPE = {
    [STBarDir.col]: CHART_TYPE.COLUMN_CHART,
    [STBarDir.bar]: CHART_TYPE.BAR_CHART,
};

export class BarChartConfigHelper extends BaseChartConfigHelper {
    public get type() {
        return BAR_CHART_TYPE[this.subChart.direction];
    }

    protected get subChart(): IBarChartProperty {
        return this.context.getSubChartModel().getSubChartProperty() as IBarChartProperty;
    }

    protected getAxis() {
        return new BarAxis(this.subChart, this.context.getChartModel()).init();
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new BarDataLabel(label, this.context);
    }

    protected getSeriesColor(): IChartRendererConfig['seriesColor'] {
        return this.series.map(ser => (ser.fill === NO_SUPPORT_FILL_SYMBOL ? '' : (ser.fill || '')));
    }

    protected getGeometryStyle(chartDataItem: ChartUIDataItem, geomType?: GeometryType): ShapeStyle {
        const { fill, border, value } = chartDataItem || {};
        let lineDash = border?.lineDash;

        // 是圆点类型，0 2，导致运算出0开头。但是0开头在非折线的图形边框无法渲染，
        // 而且office不存在圆点的边框，设置后也是路径为为1 1的规则，所以这里直接修改结果
        if (lineDash && lineDash[0] === 0 && border!.lineWidth) {
            lineDash = [1, 1].map(item => item * border!.lineWidth!);
        }

        return {
            fill,
            lineWidth: border?.lineWidth,
            lineDash,
            ...(border?.stroke ? { lineJoin: border?.lineJoin || 'round' } : {}),
            // 在空数据点的情况下，还是会显示一点stroke的颜色。所以在空数据点让stroke透明，不显示任何颜色。
            stroke: isEmptyValue(value) ? 'transparent' : border?.stroke,
            customStyles: this.getGeometryCustomStyles(chartDataItem),
        };
    }

    /**
     * 获取原始数据信息。
     * @param chartDataItem
     * @protected
     */
    protected getGeometryCustomStyles(chartDataItem: ChartUIDataItem): CustomStyles {
        const { value } = chartDataItem || {};
        const { outline = { }, ...rest } = filterOriginStyle(chartDataItem);
        const newOutline = { ...outline };  // 拷贝一份，防止原始数据被修改
        if (newOutline.color || newOutline.gradFill) { // 有填充场景
            newOutline.linejoin = newOutline.linejoin || LineJoin.Round;
        }
        // 在空数据点的情况下，还是会显示一点stroke的颜色。所以在空数据点让stroke透明，不显示任何颜色。
        if (isEmptyValue(value)) {
            newOutline.color = undefined;
            newOutline.gradFill = undefined;
            newOutline.noFill = 1;
        }
        return generateCustomStyles({ outline: newOutline, ...rest });
    }

    protected getAdjustsConfig(): IChartAdjust[]  {
        let { overlap } = this.subChart;
        const { grouping } = this.subChart;
        const adjustsOptions: any[] = [];

        if (grouping === STBarGrouping.stacked) {
            adjustsOptions.push({
                type: 'stack',
                reverseOrder: false,
            });
            overlap = overlap ?? 100;
        } else if (grouping === STBarGrouping.percentStacked) {
            adjustsOptions.push({
                type: 'stack',
                reverseOrder: false,
                isPercent: true,
            });
            overlap = overlap ?? 100;
        } else {
            overlap = overlap ?? 0;
        }
        // TODO G2 只有dodge类型支持marginRatio
        const marginRatio = overlap * -0.01;
        adjustsOptions.push({
            type: 'dodge',
            marginRatio,
        });
        return adjustsOptions;
    }

    protected getThemeConfig(): IChartRendererConfig['theme'] {
        // eslint-disable-next-line prefer-const
        const { series, gapWidth = 100, grouping } = this.subChart;
        const overlap = this.subChart.overlap
            ?? (grouping === STBarGrouping.stacked || grouping === STBarGrouping.percentStacked ? 100 : 0);
        // 之前的第一个ser有可能dataList是空的，所以要找第一个有dataList的ser(之前没问题是因为，导入保留了categoryAxisData和values)
        const firstDisplaySeries = this.series.find(ser => ser.categories && ser.categories.length > 0) || {};
        // @ts-ignore
        const { categories = [], values = [] } = firstDisplaySeries;
        const s = series.length;
        // 有可能数据上series没有category
        const c = categories.length || values.length;

        const columnWidthRatio  =  s * c /  (((-overlap * 0.01) * (s - 1) * c) + (gapWidth * 0.01 * c) + (s * c));
        return {
            columnWidthRatio,
        };
    }

    protected getCoordinateConfig() {
        const coordinate = new BarCoordinate(this.subChart);
        return coordinate.getConfig();
    }
}

class BarAxis extends Axis {
    protected getXScale(): IChartScale {
        const xScale: IChartScale = {
            min: undefined,
            max: undefined,
            type: 'cat',
        };

        return xScale;
    }

    protected getGrid(axis, type: 'xAxis' | 'yAxis'): AxisGridCfg | null {
        const grid = super.getGrid(axis, type);
        if (grid && type === 'xAxis') {
            grid.alignTick = false;
        }

        return grid;
    }

    protected getAxisPosition(position: STAxPos, reflect: boolean, allNegative?): boolean {
        if ((this.subChart as IBarChartProperty).direction  === STBarDir.bar) {
            const positionMap = reflect ? {
                [STAxPos.l]: allNegative ? 'bottom' : 'top',
                [STAxPos.r]: allNegative ? 'top' : 'bottom',
                [STAxPos.b]: 'right',
                [STAxPos.t]: 'left',
            } : {
                [STAxPos.l]: allNegative ? 'top' : 'bottom',
                [STAxPos.r]: allNegative ? 'bottom' : 'top',
                [STAxPos.b]: 'left',
                [STAxPos.t]: 'right',
            };
            return positionMap[position];
        }

        return super.getAxisPosition(position, reflect, allNegative);
    }
}

class BarDataLabel extends DataLabel {
    // eslint-disable-next-line complexity
    protected getLabelPosition(value: number, dataPointLabel: IDataLabelProperty, layout?: IChartManualLayout) {
        const presetPosition = dataPointLabel.labelPosition;
        const subChart = this.subChart as IBarChartProperty;
        const { grouping, direction } = subChart;

        // 柱形图、条形图的堆积和百分比堆积的默认位置是居中
        if (
            !presetPosition
            && (grouping === STBarGrouping.stacked || grouping === STBarGrouping.percentStacked)
        ) {
            return ChartUILabelPosition.Center;
        }

        if (direction === STBarDir.bar) {
            // 条形图 在没有位置的情况下，默认是靠右
            return labelPositionTypeMap[presetPosition || ''] || ChartUILabelPosition.OutEnd;
        }

        // 剩余处理col的情况
        // 负数outEnd展示在下方
        if (value < 0 && presetPosition === STDLblPos.outEnd) {
            return ChartUILabelPosition.Bottom;
        }

        const isAllNegative = !flatten(this.subChart.series.map(ser => ser.dataList))
            .some(item => item?.valuePoint?.value && Number(item.valuePoint.value) > 0);

        const defaultPosition = isAllNegative ? ChartUILabelPosition.Bottom : ChartUILabelPosition.Top;

        return presetPosition ? labelPositionTypeMap[presetPosition] : defaultPosition;
    }
}

class BarCoordinate extends Coordinate {
    public getConfig(): CoordinateOption {
        const { xAxis, yAxis } = this.subChartProperty;
        const actions: any[] = [];

        if ((this.subChartProperty as IBarChartProperty).direction === STBarDir.bar) {
            if (yAxis?.scaling?.orientation === STOrientationDmlC.maxMin) {
                actions.push(['reflect', 'x']);
            }
            if (xAxis?.scaling?.orientation === STOrientationDmlC.maxMin) {
                actions.push(['reflect', 'y']);
            }
        } else {
            if (yAxis?.scaling?.orientation === STOrientationDmlC.maxMin) {
                actions.push(['reflect', 'y']);
            }
            if (xAxis?.scaling?.orientation === STOrientationDmlC.maxMin) {
                actions.push(['reflect', 'x']);
            }
        }

        return {
            actions,
        };
    }
}

